export interface FirmwareUpdateRequest {
	// there will always be a status
	status: FirmwareUpdateRequestStatus;
	// executingStage is only present/defined when the status is EXECUTING
	executingStage?: FirmwareUpdateRequestStage;
	// error is only only present/defined when the status is ERRORED
	error?: FirmwareUpdateRequestError;
	progress: number;
	package: string;
	version: string;
}

export enum FirmwareUpdateRequestStatus {
	/** The request to update the device was received, but has not started yet */
	Pending = 'PENDING',

	/** The firmware package is in the process of being applied */
	Executing = 'EXECUTING',

	/** The firmware update is complete */
	Complete = 'COMPLETE',

	/** An error occurred during the firmware update or the update request was rejected */
	Errored = 'ERRORED',

	/** The firmware update was canceled */
	Canceled = 'CANCELED'
}

// Stage... When the FirmwareUpdateRequestStatus is EXECUTING,
// these are the possible stage values
export enum FirmwareUpdateRequestStage {
	/** The device is installing the firmware package */
	Installing = 'INSTALLING',

	/** The firmware package is being validated by the device before installation */
	PreInstallValidating = 'PRE_INSTALL_VALIDATING',

	/** The device is rebooting */
	Rebooting = 'REBOOTING',

	/** The device is being reserved for a firmware update */
	ReservingDevice = 'RESERVING_DEVICE',

	/** The firmware package is being transferred to the device */
	TransferringFiles = 'TRANSFERRING_FILES',

	/** The device is verifying that the firmware package was installed correctly */
	Verifying = 'VERIFYING'
}

// Error Code - When FirmwareUpdateRequestSTatus is ERRORED
// Using this const structure instead of enum due the gql spec's corresponding
// value/type is a scalar and not an enum, and this makes it more open
// to extension, and eliminates the need for some dangerous casting.
// eslint-disable-next-line @typescript-eslint/naming-convention
export const FirmwareUpdateRequestError = {
	ConnectivityLoss: 'CONNECTIVITY_LOSS',
	CorruptPackage: 'CORRUPT_PACKAGE',
	DanteLocked: 'DANTE_LOCKED',
	DanteNetworkMismatch: 'DANTE_NETWORK_MISMATCH',
	DeviceBusy: 'DEVICE_BUSY',
	DeviceNotFound: 'DEVICE_NOT_FOUND',
	DeviceRejectedPackage: 'DEVICE_REJECTED_PACKAGE',
	FileTransferError: 'FILE_TRANSFER_ERROR',
	InstallationError: 'INSTALLATION_ERROR',
	InvalidPackage: 'INVALID_PACKAGE',
	MissingProxy: 'MISSING_PROXY',
	NetworkLocked: 'NETWORK_LOCKED',
	NotConnectedViaUsb: 'NOT_CONNECTED_VIA_USB',
	NotDocked: 'NOT_DOCKED',
	QuotaExceeded: 'QUOTA_EXCEEDED',
	UpdateInProgress: 'UPDATE_IN_PROGRESS',
	VerificationError: 'VERIFICATION_ERROR',
	Unknown: 'UNKNOWN'
} as const;
export type FirmwareUpdateRequestError = (typeof FirmwareUpdateRequestError)[keyof typeof FirmwareUpdateRequestError];

export function isFirmwareUpdateRequestActive(status: FirmwareUpdateRequestStatus | undefined): boolean {
	return status === FirmwareUpdateRequestStatus.Executing;
}

export function isFirmwareUpdateRequestPending(status: FirmwareUpdateRequestStatus | undefined): boolean {
	return status === FirmwareUpdateRequestStatus.Pending;
}

export function isFirmwareUpdateRequestEnded(status: FirmwareUpdateRequestStatus | undefined): boolean {
	return (
		status === FirmwareUpdateRequestStatus.Complete ||
		status === FirmwareUpdateRequestStatus.Canceled ||
		status === FirmwareUpdateRequestStatus.Errored
	);
}

export function isFirmwareUpdateRequestSuccess(status: FirmwareUpdateRequestStatus | undefined): boolean {
	return status === FirmwareUpdateRequestStatus.Complete;
}

export function isFirmwareUpdateRequestError(status: FirmwareUpdateRequestStatus | undefined): boolean {
	return status === FirmwareUpdateRequestStatus.Errored;
}

export function isFirmwareUpdateRequestCanceled(status: FirmwareUpdateRequestStatus | undefined): boolean {
	return status === FirmwareUpdateRequestStatus.Canceled;
}

export interface FirmwareUpdateRequestFeatures {
	primaryFirmwareUpdateRequest?: { currentRequest: FirmwareUpdateRequest };
	hostedFirmwareUpdateRequest?: { currentRequest: FirmwareUpdateRequest };
}

export function isDeviceUpdating(device: { features: FirmwareUpdateRequestFeatures }): boolean {
	return (
		isFirmwareUpdateRequestPending(device.features.primaryFirmwareUpdateRequest?.currentRequest.status) ||
		isFirmwareUpdateRequestActive(device.features.primaryFirmwareUpdateRequest?.currentRequest.status) ||
		isFirmwareUpdateRequestPending(device.features.hostedFirmwareUpdateRequest?.currentRequest.status) ||
		isFirmwareUpdateRequestActive(device.features.hostedFirmwareUpdateRequest?.currentRequest.status)
	);
}
